// Single source for every UI string. Keys must exist in both languages.
export const SUPPORTED = ["es", "en"];
export const FALLBACK = "es";

export const translations = {
  es: {
    app: { name: "G.A.G Demolition", product: "Gestión interna", tagline: "Labor Services · Construction & Demolition", loading: "Cargando…" },
    common: {
      save: "Guardar", saving: "Guardando…", cancel: "Cancelar", edit: "Editar", create: "Crear", close: "Cerrar",
      active: "Activo", inactive: "Inactivo", status: "Estado", actions: "Acciones", yes: "Sí", no: "No",
      language: "Idioma", retry: "Reintentar", comingSoon: "Próximamente", optional: "opcional", search: "Buscar",
      admin: "Administrador", employee: "Empleado", menu: "Menú", logout: "Cerrar sesión",
    },
    auth: {
      title: "Iniciar sesión", subtitle: "Acceso exclusivo para el personal de G.A.G Demolition.",
      identifier: "Email o teléfono", identifierHint: "Administrador: email · Empleado: teléfono",
      password: "Contraseña", submit: "Entrar", submitting: "Entrando…",
      noSignup: "Las cuentas de empleados las crea el administrador.",
      forgot: "¿Olvidaste tu contraseña?", showPassword: "Mostrar contraseña", hidePassword: "Ocultar contraseña",
      backToLogin: "Volver al inicio de sesión",
      forgotTitle: "Recuperar contraseña", forgotSubtitle: "Escribe el email de tu cuenta y te enviaremos un enlace para crear una contraseña nueva.",
      forgotSubmit: "Enviar enlace", forgotSent: "Si el email corresponde a una cuenta, recibirás un enlace en unos minutos. Revisa también la carpeta de spam. El enlace vence en 30 minutos.",
      employeeResetNote: "¿Eres empleado y entras con tu teléfono? Pide al administrador una contraseña temporal.",
      resetTitle: "Nueva contraseña", resetSubtitle: "Elige una contraseña de al menos 8 caracteres.",
      resetSubmit: "Guardar contraseña", resetDone: "Tu contraseña se actualizó. Las sesiones anteriores se cerraron.",
      requestNewLink: "Solicitar un enlace nuevo",
      forceChangeTitle: "Cambia tu contraseña", forceChangeSubtitle: "El administrador te asignó una contraseña temporal. Crea una nueva para continuar.",
    },
    nav: {
      dashboard: "Panel", clients: "Clientes", projects: "Obras / Proyectos", employees: "Empleados",
      assignments: "Asignaciones", time_tracking: "Control de horas", payroll: "Payroll",
      labor_reports: "Reportes de horas", estimates: "Presupuestos", invoices: "Facturas",
      expenses: "Gastos", payments: "Pagos y cheques", calendar: "Agenda", ai_assistant: "Asistente G.A.G AI",
      settings: "Configuración",
      groups: { operations: "Operaciones", finance: "Finanzas", tools: "Herramientas" },
    },
    modules: {
      phaseNote: "Este módulo se desarrollará en una próxima fase. No hay datos simulados.",
      clients: "Empresas contratistas y clientes propios, con contactos y tarifas comerciales.",
      projects: "Obras y proyectos con dirección, cliente y geocerca configurable en pies.",
      assignments: "Asignación de trabajadores a obras y fechas.",
      time_tracking: "Marcaciones con GPS, correcciones manuales con contraseña y auditoría.",
      payroll: "Horas, horas extra, importe bruto, pagos parciales y saldo pendiente por empleado.",
      labor_reports: "Reportes por empresa, obra y período, en PDF en español o inglés.",
      estimates: "Presupuestos en PDF con idioma de documento independiente.",
      invoices: "Facturas, facturación parcial y prevención de facturación duplicada.",
      expenses: "Materiales y gastos por proyecto para calcular la rentabilidad.",
      payments: "Cobros, cheques recibidos, depositados y cobrados, con fechas límite.",
      calendar: "Agenda de obras, asignaciones y vencimientos.",
      ai_assistant: "Asistente bilingüe que consultará datos reales y pedirá confirmación antes de modificar nada.",
    },
    dashboard: {
      title: "Panel administrativo", welcome: "Bienvenido, {{name}}",
      activeEmployees: "Empleados activos", totalEmployees: "Empleados registrados",
      companyProfile: "Datos de la empresa", configured: "Configurados", notConfigured: "Pendiente",
      configureNow: "Configurar ahora", addEmployee: "Registrar empleado",
      modulesTitle: "Módulos", modulesSubtitle: "Los módulos marcados como Próximamente se activarán en las siguientes fases.",
      quickActions: "Acciones rápidas",
    },
    employees: {
      title: "Empleados", subtitle: "Altas rápidas. Solo el administrador crea cuentas.",
      add: "Nuevo empleado", editTitle: "Editar empleado", createTitle: "Registrar empleado",
      firstName: "Nombre", lastName: "Apellido", phone: "Teléfono", phoneHint: "10 dígitos (EE. UU.). Se usa para iniciar sesión.",
      password: "Contraseña de acceso", passwordHint: "Mínimo 8 caracteres.", newPassword: "Nueva contraseña",
      newPasswordHint: "Déjalo vacío para mantener la actual. Si la cambias, será temporal: el empleado deberá crear una nueva al entrar.", activeLabel: "Cuenta activa",
      empty: "Todavía no hay empleados registrados.", emptyHint: "Registra al primer empleado para darle acceso al portal.",
      created: "Empleado registrado", updated: "Empleado actualizado", rateNote: "La tarifa por hora se configurará en el módulo de Payroll.",
      count: "{{count}} empleados",
    },
    settings: {
      title: "Configuración", subtitle: "Datos comerciales que aparecerán en los documentos.",
      company: "Empresa", documents: "Documentos", security: "Seguridad",
      legalName: "Nombre legal", address: "Dirección", phone: "Teléfono", email: "Email", website: "Sitio web",
      taxId: "EIN / Tax ID", license: "Licencia", additional: "Información adicional",
      saved: "Configuración guardada", notConfiguredBanner: "Introduce los datos reales de la empresa. No se usará información inventada.",
      docLangTitle: "Idioma de documentos", docLangHelp: "Cada Presupuesto, Factura y Reporte de horas tendrá su propio idioma. Este es solo el valor predeterminado y no cambia el idioma de la interfaz.",
      defaultDocLang: "Idioma predeterminado de documentos", previewLang: "Vista previa de etiquetas",
      previewHelp: "Etiquetas estándar que se imprimirán en el PDF en el idioma elegido.",
      changePassword: "Cambiar contraseña", currentPassword: "Contraseña actual", temporaryPassword: "Contraseña temporal", newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña", passwordChanged: "Contraseña actualizada",
      langES: "Español (ES)", langEN: "Inglés (EN)",
    },
    portal: {
      home: "Inicio", clock: "Fichar", hours: "Horas", earnings: "Pagos", profile: "Perfil",
      hello: "Hola, {{name}}", summary: "Tu resumen",
      hoursWorked: "Horas trabajadas", daysWorked: "Días trabajados", gross: "Importe generado", pending: "Pendiente de cobro",
      paid: "Pagos recibidos",
      clockTitle: "Clock In / Clock Out", clockIn: "Clock In", clockOut: "Clock Out",
      clockSoon: "El fichaje con GPS se activará en la fase de Time Tracking.",
      hoursTitle: "Mis horas y obras", hoursEmpty: "Aún no tienes horas registradas.",
      earningsTitle: "Mis ingresos y pagos", earningsEmpty: "Aún no hay pagos registrados.",
      profileTitle: "Mi perfil", name: "Nombre", phone: "Teléfono", role: "Rol",
    },
    validation: {
      required: "Este campo es obligatorio.", phone: "Introduce un teléfono de 10 dígitos.",
      minPassword: "La contraseña debe tener al menos 8 caracteres.", email: "Email no válido.",
      mismatch: "Las contraseñas no coinciden.",
    },
    errors: {
      invalid_credentials: "Credenciales incorrectas.", account_inactive: "Tu cuenta está inactiva. Contacta al administrador.",
      too_many_attempts: "Demasiados intentos. Espera 15 minutos.", not_authenticated: "Inicia sesión para continuar.",
      session_expired: "Tu sesión expiró. Vuelve a iniciar sesión.", forbidden: "No tienes permiso para acceder.",
      phone_in_use: "Ese teléfono ya está registrado.", wrong_password: "La contraseña actual es incorrecta.",
      not_found: "Registro no encontrado.", validation: "Revisa los datos del formulario.",
      network: "No se pudo conectar con el servidor.", generic: "Ocurrió un error. Inténtalo de nuevo.",
      loadFailed: "No se pudieron cargar los datos.",
      invalid_token: "El enlace no es válido, ya se usó o venció.", must_change_password: "Debes cambiar tu contraseña para continuar.", same_password: "La contraseña nueva debe ser distinta a la actual.",
    },
    notFound: { title: "Página no encontrada", back: "Volver al inicio" },
    install: {
      button: "Instalar aplicación", title: "Para instalar G.A.G Demolition:",
      iosStep1: "Abrí la aplicación en Safari.", iosStep2: "Tocá Compartir.",
      iosStep3: "Seleccioná Añadir a pantalla de inicio.", iosStep4: "Confirmá Añadir.",
      otherIntro: "En iPhone y iPad la instalación solo está disponible desde Safari.",
      otherStep1: "Copiá el enlace con el botón de abajo.", otherStep2: "Abrí Safari y pegá el enlace en la barra de direcciones.",
      copyLink: "Copiar enlace", linkCopied: "Enlace copiado",
      genericStep1: "Abrí el menú del navegador.", genericStep2: "Elegí Instalar aplicación o Añadir a pantalla de inicio.",
    },
  },
  en: {
    app: { name: "G.A.G Demolition", product: "Internal management", tagline: "Labor Services · Construction & Demolition", loading: "Loading…" },
    common: {
      save: "Save", saving: "Saving…", cancel: "Cancel", edit: "Edit", create: "Create", close: "Close",
      active: "Active", inactive: "Inactive", status: "Status", actions: "Actions", yes: "Yes", no: "No",
      language: "Language", retry: "Retry", comingSoon: "Coming soon", optional: "optional", search: "Search",
      admin: "Admin", employee: "Employee", menu: "Menu", logout: "Log out",
    },
    auth: {
      title: "Sign in", subtitle: "Restricted to G.A.G Demolition staff.",
      identifier: "Email or phone", identifierHint: "Admin: email · Employee: phone",
      password: "Password", submit: "Sign in", submitting: "Signing in…",
      noSignup: "Employee accounts are created by the administrator.",
      forgot: "Forgot your password?", showPassword: "Show password", hidePassword: "Hide password",
      backToLogin: "Back to sign in",
      forgotTitle: "Reset password", forgotSubtitle: "Enter your account email and we'll send you a link to create a new password.",
      forgotSubmit: "Send link", forgotSent: "If the email belongs to an account, you'll receive a link in a few minutes. Check your spam folder too. The link expires in 30 minutes.",
      employeeResetNote: "Employee signing in with a phone? Ask the administrator for a temporary password.",
      resetTitle: "New password", resetSubtitle: "Choose a password with at least 8 characters.",
      resetSubmit: "Save password", resetDone: "Your password was updated. Previous sessions were signed out.",
      requestNewLink: "Request a new link",
      forceChangeTitle: "Change your password", forceChangeSubtitle: "The administrator assigned you a temporary password. Create a new one to continue.",
    },
    nav: {
      dashboard: "Dashboard", clients: "Clients", projects: "Job Sites / Projects", employees: "Employees",
      assignments: "Labor Assignments", time_tracking: "Time Tracking", payroll: "Payroll",
      labor_reports: "Client Labor Reports", estimates: "Estimates", invoices: "Invoices",
      expenses: "Expenses", payments: "Payments & Checks", calendar: "Calendar", ai_assistant: "G.A.G AI Assistant",
      settings: "Settings",
      groups: { operations: "Operations", finance: "Finance", tools: "Tools" },
    },
    modules: {
      phaseNote: "This module will be built in an upcoming phase. No simulated data is shown.",
      clients: "Contractor companies and own-project clients, with contacts and billing rates.",
      projects: "Job sites and projects with address, client and a geofence radius in feet.",
      assignments: "Assign workers to job sites and dates.",
      time_tracking: "GPS clock punches, password-confirmed manual corrections and audit log.",
      payroll: "Hours, overtime, gross pay, partial payments and pending balance per employee.",
      labor_reports: "Reports by company, job site and period, as PDF in English or Spanish.",
      estimates: "PDF estimates with an independent document language.",
      invoices: "Invoices, partial billing and duplicate-billing prevention.",
      expenses: "Materials and expenses per project to measure profitability.",
      payments: "Collections, checks received, deposited and cleared, with deadlines.",
      calendar: "Schedule for job sites, assignments and due dates.",
      ai_assistant: "Bilingual assistant that will query real data and ask for confirmation before changing anything.",
    },
    dashboard: {
      title: "Admin dashboard", welcome: "Welcome, {{name}}",
      activeEmployees: "Active employees", totalEmployees: "Registered employees",
      companyProfile: "Company details", configured: "Configured", notConfigured: "Pending",
      configureNow: "Configure now", addEmployee: "Add employee",
      modulesTitle: "Modules", modulesSubtitle: "Modules marked Coming soon will be enabled in the next phases.",
      quickActions: "Quick actions",
    },
    employees: {
      title: "Employees", subtitle: "Quick onboarding. Only the admin creates accounts.",
      add: "New employee", editTitle: "Edit employee", createTitle: "Add employee",
      firstName: "First name", lastName: "Last name", phone: "Phone", phoneHint: "10 digits (US). Used to sign in.",
      password: "Access password", passwordHint: "At least 8 characters.", newPassword: "New password",
      newPasswordHint: "Leave empty to keep the current one. If changed, it's temporary: the employee must create a new one at next sign-in.", activeLabel: "Active account",
      empty: "No employees registered yet.", emptyHint: "Add the first employee to give them portal access.",
      created: "Employee added", updated: "Employee updated", rateNote: "Hourly rate will be set in the Payroll module.",
      count: "{{count}} employees",
    },
    settings: {
      title: "Settings", subtitle: "Business details that will appear on documents.",
      company: "Company", documents: "Documents", security: "Security",
      legalName: "Legal name", address: "Address", phone: "Phone", email: "Email", website: "Website",
      taxId: "EIN / Tax ID", license: "License", additional: "Additional information",
      saved: "Settings saved", notConfiguredBanner: "Enter the company's real details. No invented information will be used.",
      docLangTitle: "Document language", docLangHelp: "Each Estimate, Invoice and Labor Report has its own language. This is only the default and does not change the interface language.",
      defaultDocLang: "Default document language", previewLang: "Label preview",
      previewHelp: "Standard labels printed on the PDF in the chosen language.",
      changePassword: "Change password", currentPassword: "Current password", temporaryPassword: "Temporary password", newPassword: "New password",
      confirmPassword: "Confirm password", passwordChanged: "Password updated",
      langES: "Spanish (ES)", langEN: "English (EN)",
    },
    portal: {
      home: "Home", clock: "Clock", hours: "Hours", earnings: "Pay", profile: "Profile",
      hello: "Hi, {{name}}", summary: "Your summary",
      hoursWorked: "Hours worked", daysWorked: "Days worked", gross: "Gross earned", pending: "Pending balance",
      paid: "Payments received",
      clockTitle: "Clock In / Clock Out", clockIn: "Clock In", clockOut: "Clock Out",
      clockSoon: "GPS clock-in will be enabled in the Time Tracking phase.",
      hoursTitle: "My hours & job sites", hoursEmpty: "You have no recorded hours yet.",
      earningsTitle: "My earnings & payments", earningsEmpty: "No payments recorded yet.",
      profileTitle: "My profile", name: "Name", phone: "Phone", role: "Role",
    },
    validation: {
      required: "This field is required.", phone: "Enter a 10-digit phone number.",
      minPassword: "Password must be at least 8 characters.", email: "Invalid email.",
      mismatch: "Passwords do not match.",
    },
    errors: {
      invalid_credentials: "Invalid credentials.", account_inactive: "Your account is inactive. Contact the administrator.",
      too_many_attempts: "Too many attempts. Wait 15 minutes.", not_authenticated: "Please sign in to continue.",
      session_expired: "Your session expired. Please sign in again.", forbidden: "You don't have permission.",
      phone_in_use: "That phone is already registered.", wrong_password: "Current password is incorrect.",
      not_found: "Record not found.", validation: "Please check the form fields.",
      network: "Could not reach the server.", generic: "Something went wrong. Please try again.",
      loadFailed: "Could not load data.",
      invalid_token: "This link is invalid, already used or expired.", must_change_password: "You must change your password to continue.", same_password: "The new password must be different from the current one.",
    },
    notFound: { title: "Page not found", back: "Back to start" },
    install: {
      button: "Install App", title: "To install G.A.G Demolition:",
      iosStep1: "Open the app in Safari.", iosStep2: "Tap Share.",
      iosStep3: "Select Add to Home Screen.", iosStep4: "Confirm Add.",
      otherIntro: "On iPhone and iPad, installing is only available from Safari.",
      otherStep1: "Copy the link with the button below.", otherStep2: "Open Safari and paste the link in the address bar.",
      copyLink: "Copy link", linkCopied: "Link copied",
      genericStep1: "Open the browser menu.", genericStep2: "Choose Install app or Add to Home Screen.",
    },
  },
};
